import { useEffect, useState } from 'react';

// Types
import type { UserType } from '@/types/userType';
import type { AnalyticsPeriodSelectedType } from '@/utils/hooks/analytics/usePeriodAnalytics';
import type { GroupGlobalTableConfigType } from '@/lib/analyticsGroup/tables/groupGlobalTableConfig.type';
import type { DataApiDateType } from '@/utils/hooks/swr/group/useGroupGlobalTableAnalytics/types';
// Hooks
import { getVisibleColumns } from '@/utils/table/getColumnOrder';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useGroupAnalytics from '@/utils/hooks/swr/group/useGroupGlobalTableAnalytics';
import useTableColumnState from '@/utils/hooks/table/useTableColumnState';

// Components
import GroupGlobalTableContent from '@/components/pages/analyze-group/tables/GroupGlobalTable/GroupGlobalTableContent';
import ErrorAnalyticsFallback from '@/components/layouts/ErrorFallbacks/ErrorAnalyticsFallback';

export type PeriodSelectedType = AnalyticsPeriodSelectedType & {
    previousPeriod: DataApiDateType;
};

export type GroupGlobalTableProps = {
    config: GroupGlobalTableConfigType;
    periodSelected: AnalyticsPeriodSelectedType;
    userDataFormat: Pick<
        UserType['settings'],
        'numbers_format' | 'date_format' | 'timezone' | 'time_format'
    >;
};

function getVisibleColumnsExceptAvatar<TColumnVisibility>(columnVisibility: TColumnVisibility) {
    return getVisibleColumns(columnVisibility).filter(
        (column) => column !== 'avatar'
    ) as (keyof Omit<TColumnVisibility, 'avatar'>)[];
}

const GroupGlobalTable = ({ config, periodSelected, userDataFormat }: GroupGlobalTableProps) => {
    const { activeGroup } = useActiveGroup();

    // Columns visibility
    const { columnOrder, columnVisibility, setColumnVisibility, setColumnOrder } =
        useTableColumnState({
            columnsVisibility: config.columnsVisibility,
            nameCookieColumnOrder: `group_${activeGroup.id}_${config.id}_column_order`,
            nameCookieColumnVisibility: `group_${activeGroup.id}_${config.id}_column_visibility`
        });

    const [fields, setFields] = useState(getVisibleColumnsExceptAvatar(columnVisibility));

    useEffect(() => {
        setFields(getVisibleColumnsExceptAvatar(columnVisibility));
    }, [columnVisibility]);

    const {
        groupAnalytics,
        isLoading,
        groupAnalyticsError,
        mutateGroupAnalytics,
        comparisonDates
    } = useGroupAnalytics({
        groupId: activeGroup.id,
        params: {
            period: periodSelected.period,
            comparison_period: periodSelected.compareTo,
            start: periodSelected.start,
            end: periodSelected.end,
            fields: fields
        },
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    if (groupAnalyticsError) {
        return (
            <div className="h-[448px]">
                <ErrorAnalyticsFallback
                    resetErrorBoundary={() => mutateGroupAnalytics(undefined)}
                />
            </div>
        );
    }

    return (
        <GroupGlobalTableContent
            activeGroup={activeGroup}
            groupAnalytics={groupAnalytics}
            isDataLoading={isLoading}
            config={config}
            userDataFormat={userDataFormat}
            periodSelected={{
                ...periodSelected,
                previousPeriod: {
                    start: comparisonDates?.previous?.start,
                    end: comparisonDates?.previous?.end
                }
            }}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            setColumnOrder={setColumnOrder}
            setColumnVisibility={setColumnVisibility}
        />
    );
};

export default GroupGlobalTable;
